import { Injectable } from '@angular/core';
import { ShellQuery } from '@shared/store/shell.query';
import { createInitialState, ShellStore } from '@shared/store/shell.store';
import { IShellContext, IShellContextBizCtx, IShellContextUI } from '@shared/store/IShellContext';
import { map, Observable, Subject, take, tap } from 'rxjs';
import { RoleTypeEnum } from '@shared/enums';
import _ from 'lodash';
import { IDisplayedRxForm } from '@shared/generalInterfaces';
import { Order } from '@shared/orders/order';

@Injectable({ providedIn: 'root' })
export class ShellContextService {
	selectedLanguage$: Observable<string> = this.shellQuery.selectedLanguage$;

	contextBizContextChanged$ = new Subject<IShellContextBizCtx>();
	contextUiChanged$ = new Subject<IShellContextUI>();

	constructor(
		private shellQuery: ShellQuery,
		private shellStore: ShellStore
	) {}

	get selectedCompanyId$(): Observable<number> {
		return this.shellQuery.selectedCompanyId$;
	}

	get selectedContactId$(): Observable<number> {
		return this.shellQuery.selectedContactId$;
	}

	get roleType$(): Observable<RoleTypeEnum> {
		return this.shellQuery.roleType$;
	}

	get isAuth0$(): Observable<boolean> {
		return this.shellQuery.isAuth0$;
	}

	getContext(): Observable<IShellContext> {
		return this.shellQuery.context$;
	}

	getOrder(): Observable<Order | IDisplayedRxForm> {
		return this.shellQuery.order$;
	}

	updateOrder(order: Order | IDisplayedRxForm): void {
		this.shellStore.update(state => {
			state.order = order;
		});
	}

	updateIsAuth0(isAuth0: boolean): void {
		this.shellStore.update(state => {
			state.isAuth0 = isAuth0;
		});
	}

	isContextReady(): Observable<boolean> {
		return this.shellQuery.context$.pipe(
			map(
				(context: IShellContext) =>
					context?.bizCtx?.region?.length > 0 &&
					context?.bizCtx?.companyId > 0 &&
					context?.bizCtx?.contactId > 0 &&
					context?.security?.accessToken?.length > 0
			)
		);
	}

	updateContext(contextCallback: (context: IShellContext) => void): void;
	updateContext(partialContext: Partial<IShellContext>): void;
	updateContext(contextOrCallback: ((context: IShellContext) => void) | Partial<IShellContext>): void {
		if (typeof contextOrCallback === 'function') {
			this.shellStore.update(state => {
				const originalBizCtxSection = _.cloneDeep(state.context.bizCtx);
				const originalUiSection = _.cloneDeep(state.context.UI);

				contextOrCallback(state.context);
				this.triggerContextBizCtxUpdatedIfNeed(originalBizCtxSection, _.cloneDeep(state.context.bizCtx));
				this.triggerContextUiUpdatedIfNeed(originalUiSection, _.cloneDeep(state.context.UI));
			});
		} else {
			this.shellStore.update(state => {
				const originalBizCtxSection = _.cloneDeep(state.context.bizCtx);
				const originalUiSection = _.cloneDeep(state.context.UI);

				state.context = { ...state.context, ...contextOrCallback };
				this.triggerContextBizCtxUpdatedIfNeed(originalBizCtxSection, _.cloneDeep(state.context.bizCtx));
				this.triggerContextUiUpdatedIfNeed(originalUiSection, _.cloneDeep(state.context.UI));
			});
		}
	}

	private triggerContextBizCtxUpdatedIfNeed(originalBizCtxSection: IShellContextBizCtx, newBizCtxSection: IShellContextBizCtx) {
		const bizCtxChanged = !_.isEqual(originalBizCtxSection, newBizCtxSection);

		if (bizCtxChanged) {
			this.contextBizContextChanged$.next(newBizCtxSection);
		}
	}

	private triggerContextUiUpdatedIfNeed(originalUiSection: IShellContextUI, newUiSection: IShellContextUI) {
		const uiSectionChanged = !_.isEqual(originalUiSection, newUiSection);

		if (uiSectionChanged) {
			this.contextUiChanged$.next(newUiSection);
		}
	}

	resetContext() {
		this.shellStore.update(state => {
			state.context = createInitialState().context;
			state.order = createInitialState().order;
			state.isAuth0 = createInitialState().isAuth0;
		});
	}

	resetContextWithoutSession() {
		this.getContext()
			.pipe(
				take(1),
				tap(context => {
					const sessionSection = context.session;
					const securitySection = context.security;

					this.shellStore.update(state => {
						state.context = createInitialState().context;
						state.context.session = sessionSection;
						state.context.security = securitySection;
					});
				})
			)
			.subscribe();
	}

	resetOrderData(resetPatientId: boolean = true) {
		let orderData;

		if (resetPatientId) {
			orderData = { orderId: null, csmMessageId: null, patientId: null, rxId: null };
		} else {
			orderData = { orderId: null, csmMessageId: null, rxId: null };
		}

		this.shellStore.update(state => {
			state.context.bizCtx = { ...state.context.bizCtx, ...orderData };
			state.order = null;
		});
	}
}
